import React, { useState, useEffect, useRef } from "react";
import styles from "./OpacityComparison.module.css";

// Helpers
import cloneDeep from "lodash.clonedeep";
import Draggable from "react-draggable";
import { getImageSrc, handleImageLoad } from "../../helpers/imageLoadUtils";

// Custom components
import { SquareIconUtilityButton } from "../Buttons/Buttons";
import { IconValueSlider } from "../Sliders/Sliders";

// Icons
import { ReactComponent as ZoomInIcon } from "./Icons/zoom_in.svg";
import { ReactComponent as SliderIcon } from "./Icons/slider.svg";
import { ReactComponent as OpacityIcon } from "./Icons/opacity.svg";
import { ReactComponent as RotateIcon } from "./Icons/rotate.svg";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

function OpacityComparison(props) {
  const rotationRange = 90;
  const imageMinZoom = 10;
  const imageMaxZoom = 1000;
  const zoomDampener = 3000;

  const {
    currentComparisonIndex,
    comparisonItems,
    setComparisonItems,
    updatePositionOneOpacity,
    updatePositionTwoOpacity,
    handleStart,
    touchControls,
  } = props;

  const pairData = comparisonItems[currentComparisonIndex];

  const [imageOneSrc, setImageOneSrc] = useState("");
  useEffect(() => {
    setImageOneSrc(getImageSrc(pairData.fileOne[0]));
  }, []);

  const [imageTwoSrc, setImageTwoSrc] = useState("");
  useEffect(() => {
    setImageTwoSrc(getImageSrc(pairData.fileTwo[0]));
  }, []);

  const [imageOneLoading, setImageOneLoading] = useState(true);
  const [imageTwoLoading, setImageTwoLoading] = useState(true);

  // Image styles
  const imageOneStyle = {
    overflow: "hidden",
    height: pairData.imageZoomOpacity * pairData.imageOneZoomOpacity + "%",
    width: "auto",
    maxHeight: "5000%",
    maxWidth: "5000%",
    opacity: pairData.imageOneOpacity + "%",
    transitionProperty: "none",
    clipPath: `polygon(0 0, ${pairData.imageOneWidth}% 0%, ${pairData.imageOneWidth}% 100%, 0% 100%)`,
    rotate: `${pairData.imageOneRotation}deg`,

    position: "absolute",
    cursor: "pointer",
  };

  const imageTwoStyle = {
    overflow: "hidden",
    height: pairData.imageZoomOpacity * pairData.imageTwoZoomOpacity + "%",
    width: "auto",
    maxHeight: "5000%",
    maxWidth: "5000%",
    opacity: pairData.imageTwoOpacity + "%",
    clipPath: `polygon(0 0, ${pairData.imageTwoWidth}% 0%, ${pairData.imageTwoWidth}% 100%, 0% 100%)`,
    transitionProperty: "none",
    rotate: `${pairData.imageTwoRotation}deg`,
    position: "absolute",
    cursor: "pointer",
  };

  // Image position handling
  const imageOneOffset = () => {
    return pairData.lockToggleOpacity
      ? {
          x:
            getCancelledX(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageOneRotation
            ) +
            getX(
              pairData.imageOnePositionOpacity.x,
              pairData.imageOnePositionOpacity.y,
              pairData.imageOneRotation
            ) *
              (pairData.imageZoomOpacity / 100),
          y:
            getCancelledY(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageOneRotation
            ) +
            getY(
              pairData.imageOnePositionOpacity.x,
              pairData.imageOnePositionOpacity.y,
              pairData.imageOneRotation
            ) *
              (pairData.imageZoomOpacity / 100),
        }
      : {
          x:
            getCancelledX(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageOneRotation
            ) +
            getX(
              pairData.imageOnePositionOpacity.x,
              pairData.imageOnePositionOpacity.y,
              pairData.imageOneRotation
            ) *
              (pairData.imageZoomOpacity / 100) -
            pairData.imageOnePositionOpacity.x +
            pairData.positionOpacity.x,
          y:
            getCancelledY(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageOneRotation
            ) +
            getY(
              pairData.imageOnePositionOpacity.x,
              pairData.imageOnePositionOpacity.y,
              pairData.imageOneRotation
            ) *
              (pairData.imageZoomOpacity / 100) -
            pairData.imageOnePositionOpacity.y +
            pairData.positionOpacity.y,
        };
  };
  const imageOnePosition = () => {
    return pairData.lockToggleOpacity
      ? {
          x: pairData.positionOpacity.x,
          y: pairData.positionOpacity.y,
        }
      : {
          x: pairData.imageOnePositionOpacity.x,
          y: pairData.imageOnePositionOpacity.y,
        };
  };

  const imageTwoOffset = () => {
    return pairData.lockToggleOpacity
      ? {
          x:
            getCancelledX(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageTwoRotation
            ) +
            getX(
              pairData.imageTwoPositionOpacity.x,
              pairData.imageTwoPositionOpacity.y,
              pairData.imageTwoRotation
            ) *
              (pairData.imageZoomOpacity / 100),
          y:
            getCancelledY(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageTwoRotation
            ) +
            getY(
              pairData.imageTwoPositionOpacity.x,
              pairData.imageTwoPositionOpacity.y,
              pairData.imageTwoRotation
            ) *
              (pairData.imageZoomOpacity / 100),
        }
      : {
          x:
            getCancelledX(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageTwoRotation
            ) +
            getX(
              pairData.imageTwoPositionOpacity.x,
              pairData.imageTwoPositionOpacity.y,
              pairData.imageTwoRotation
            ) *
              (pairData.imageZoomOpacity / 100) -
            pairData.imageTwoPositionOpacity.x +
            pairData.positionOpacity.x,
          y:
            getCancelledY(
              pairData.positionOpacity.x,
              pairData.positionOpacity.y,
              pairData.imageTwoRotation
            ) +
            getY(
              pairData.imageTwoPositionOpacity.x,
              pairData.imageTwoPositionOpacity.y,
              pairData.imageTwoRotation
            ) *
              (pairData.imageZoomOpacity / 100) -
            pairData.imageTwoPositionOpacity.y +
            pairData.positionOpacity.y,
        };
  };
  const imageTwoPosition = () => {
    return pairData.lockToggleOpacity
      ? {
          x: pairData.positionOpacity.x,
          y: pairData.positionOpacity.y,
        }
      : {
          x: pairData.imageTwoPositionOpacity.x,
          y: pairData.imageTwoPositionOpacity.y,
        };
  };

  const getX = (originalX, originalY, rotation) => {
    const radians = rotation * (Math.PI / 180);
    const finalX =
      originalX * Math.cos(radians) + originalY * Math.sin(radians);
    return finalX;
  };

  const getY = (originalX, originalY, rotation) => {
    const radians = rotation * (Math.PI / 180);
    const finalY =
      -originalX * Math.sin(radians) + originalY * Math.cos(radians);
    return finalY;
  };

  const getCancelledX = (originalX, originalY, rotation) => {
    return getX(originalX, originalY, rotation) - originalX;
  };

  const getCancelledY = (originalX, originalY, rotation) => {
    return getY(originalX, originalY, rotation) - originalY;
  };

  // Handle image scroll zooming
  const handleScrollZoom = (event) => {
    if (
      pairData.imageZoomOpacity <= imageMaxZoom &&
      pairData.imageZoomOpacity >= imageMinZoom
    ) {
      if (
        pairData.imageZoomOpacity -
          event.deltaY * (pairData.imageZoomOpacity / zoomDampener) <=
          imageMaxZoom &&
        pairData.imageZoomOpacity -
          event.deltaY * (pairData.imageZoomOpacity / zoomDampener) >=
          imageMinZoom
      ) {
        const updatedComparisonItems = cloneDeep([...comparisonItems]);
        updatedComparisonItems[currentComparisonIndex].imageZoomOpacity =
          pairData.imageZoomOpacity -
          event.deltaY * (pairData.imageZoomOpacity / zoomDampener);
        setComparisonItems(updatedComparisonItems);
      }
    } else if (pairData.imageZoomOpacity <= imageMinZoom) {
      const updatedComparisonItems = cloneDeep([...comparisonItems]);
      updatedComparisonItems[currentComparisonIndex].imageZoomOpacity =
        imageMinZoom;
      setComparisonItems(updatedComparisonItems);
    } else if (pairData.imageZoomOpacity >= imageMaxZoom) {
      const updatedComparisonItems = cloneDeep([...comparisonItems]);
      updatedComparisonItems[currentComparisonIndex].imageZoomOpacity =
        imageMaxZoom;
      setComparisonItems(updatedComparisonItems);
    }
  };

  // Button and slider functions
  const handleResetOne = () => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageOneZoomOpacity = 1;
    updatedComparisonItems[currentComparisonIndex].imageOnePositionOpacity = {
      x: 0,
      y: 0,
    };
    updatedComparisonItems[currentComparisonIndex].imageOneRotation = 0;
    setComparisonItems(updatedComparisonItems);
  };
  const handleOpacityOne = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageOneOpacity =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleCropOne = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageOneWidth =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleRotateOne = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageOneRotation =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleZoomOne = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageOneZoomOpacity =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };

  const handleResetTwo = () => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageTwoZoomOpacity = 1;
    updatedComparisonItems[currentComparisonIndex].imageTwoPositionOpacity = {
      x: 0,
      y: 0,
    };
    updatedComparisonItems[currentComparisonIndex].imageTwoRotation = 0;
    setComparisonItems(updatedComparisonItems);
  };
  const handleOpacityTwo = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageTwoOpacity =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleCropTwo = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageTwoWidth =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleRotateTwo = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageTwoRotation =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };
  const handleZoomTwo = (event) => {
    const updatedComparisonItems = cloneDeep([...comparisonItems]);
    updatedComparisonItems[currentComparisonIndex].imageTwoZoomOpacity =
      event.target.value;

    setComparisonItems(updatedComparisonItems);
  };

  // Touch Controls START

  const initialDistanceOne = useRef(0);
  const initialDistanceTwo = useRef(0);
  const initialDistancePair = useRef(0);

  const initialAngleOne = useRef(0);
  const initialAngleTwo = useRef(0);

  const createTouchHandlers = (
    initialDistanceRef,
    zoomKey,
    initialAngleRef,
    rotationKey
  ) => {
    const handleTouchStart = (event) => {
      if (event.touches.length === 2) {
        initialDistanceRef.current = getDistance(
          event.touches[0],
          event.touches[1]
        );
        if (initialAngleRef && rotationKey) {
          // Only compute the initial angle if rotationKey is provided
          initialAngleRef.current = getAngle(
            event.touches[0],
            event.touches[1]
          );
        }
      }
    };

    const handleTouchMove = (event) => {
      if (event.touches.length === 2) {
        const currentDistance = getDistance(event.touches[0], event.touches[1]);
        const scaleChange = currentDistance / initialDistanceRef.current;

        const newScale = pairData[zoomKey] * scaleChange;
        const updatedComparisonItems = cloneDeep([...comparisonItems]);

        // Keep image zoom within bounds
        if (zoomKey === "imageZoomOpacity") {
          // console.log("imageZoomOpacity");
          // console.log(newScale);
          if (newScale >= 10 && newScale <= 1000) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = newScale;
          } else if (newScale < 10) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = 10;
          } else if (newScale > 1000) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = 1000;
          }
        } else {
          // console.log("imageZoomOneOpacity / imageZoomTwoOpacity");
          // console.log(newScale);
          if (newScale >= 0.1 && newScale <= 2) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = newScale;
          } else if (newScale < 0.1) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = 0.1;
          } else if (newScale > 2) {
            updatedComparisonItems[currentComparisonIndex][zoomKey] = 2;
          }
        }

        if (initialAngleRef && rotationKey) {
          // Apply rotation update if rotationKey is provided
          const currentAngle = getAngle(event.touches[0], event.touches[1]);
          const angleDifference = currentAngle - initialAngleRef.current;
          updatedComparisonItems[currentComparisonIndex][rotationKey] =
            parseFloat(
              updatedComparisonItems[currentComparisonIndex][rotationKey]
            ) + angleDifference;
          initialAngleRef.current = currentAngle;
        }

        setComparisonItems(updatedComparisonItems);
        initialDistanceRef.current = currentDistance;
      }
    };

    return { handleTouchStart, handleTouchMove };
  };

  const {
    handleTouchStart: handleTouchStartOne,
    handleTouchMove: handleTouchMoveOne,
  } = createTouchHandlers(
    initialDistanceOne,
    "imageOneZoomOpacity",
    initialAngleOne,
    "imageOneRotation"
  );
  const {
    handleTouchStart: handleTouchStartTwo,
    handleTouchMove: handleTouchMoveTwo,
  } = createTouchHandlers(
    initialDistanceTwo,
    "imageTwoZoomOpacity",
    initialAngleTwo,
    "imageTwoRotation"
  );
  const {
    handleTouchStart: handleTouchStartPair,
    handleTouchMove: handleTouchMovePair,
  } = createTouchHandlers(initialDistancePair, "imageZoomOpacity");

  const getDistance = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getAngle = (touch1, touch2) => {
    const dx = touch2.clientX - touch1.clientX;
    const dy = touch2.clientY - touch1.clientY;
    return (Math.atan2(dy, dx) * 180) / Math.PI;
  };

  // Touch Controls END

  const SliderLabelIcon = ({ Icon }) => {
    return (
      <div className="px-1">
        <Icon className="h-7 w-7 fill-neutral-400" />
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex flex-row items-center justify-center w-full">
        <div className="flex flex-col items-center justify-center w-full">
          <div className="flex flex-row items-center justify-center w-full py-1">
            <SquareIconUtilityButton
              Icon={ArrowPathIcon}
              iconSize="h-6 w-6"
              activeColor={"active:bg-neutral-200"}
              actionFunction={handleResetOne}
            />
            <SliderLabelIcon Icon={OpacityIcon} />
            <IconValueSlider
              sliderValue={pairData.imageOneOpacity}
              sliderMin={0}
              sliderMax={100}
              sliderStep={0.01}
              onChangeFunction={handleOpacityOne}
              showIcon={false}
              showValue={false}
            />
            <SliderLabelIcon Icon={SliderIcon} />
            <IconValueSlider
              sliderValue={pairData.imageOneWidth}
              sliderMin={0}
              sliderMax={100}
              sliderStep={0.01}
              onChangeFunction={handleCropOne}
              showIcon={false}
              showValue={false}
            />
          </div>
          <div className="flex flex-row items-center justify-center w-full">
            <div className="w-8 min-w-[2rem] h-8 min-h-[2rem]" />
            <SliderLabelIcon Icon={RotateIcon} />
            <IconValueSlider
              sliderValue={pairData.imageOneRotation}
              sliderMin={-rotationRange}
              sliderMax={rotationRange}
              sliderStep={0.01}
              onChangeFunction={handleRotateOne}
              showIcon={false}
              showValue={false}
            />
            <SliderLabelIcon Icon={ZoomInIcon} />
            <IconValueSlider
              sliderValue={pairData.imageOneZoomOpacity}
              sliderMin={0.1}
              sliderMax={2}
              sliderStep={0.001}
              onChangeFunction={handleZoomOne}
              showIcon={false}
              showValue={false}
            />
          </div>
        </div>

        <div className="px-2" />

        <div className="flex flex-col items-center justify-center w-full">
          <div className="flex flex-row items-center justify-center w-full">
            <SquareIconUtilityButton
              Icon={ArrowPathIcon}
              iconSize="h-6 w-6"
              activeColor={"active:bg-neutral-200"}
              actionFunction={handleResetTwo}
            />
            <SliderLabelIcon Icon={OpacityIcon} />
            <IconValueSlider
              sliderValue={pairData.imageTwoOpacity}
              sliderMin={0}
              sliderMax={100}
              sliderStep={0.01}
              onChangeFunction={handleOpacityTwo}
              showIcon={false}
              showValue={false}
            />
            <SliderLabelIcon Icon={SliderIcon} />
            <IconValueSlider
              sliderValue={pairData.imageTwoWidth}
              sliderMin={0}
              sliderMax={100}
              sliderStep={0.01}
              onChangeFunction={handleCropTwo}
              showIcon={false}
              showValue={false}
            />
          </div>
          <div className="flex flex-row items-center justify-center w-full">
            <div className="w-8 min-w-[2rem] h-8 min-h-[2rem]" />
            <SliderLabelIcon Icon={RotateIcon} />
            <IconValueSlider
              sliderValue={pairData.imageTwoRotation}
              sliderMin={-rotationRange}
              sliderMax={rotationRange}
              sliderStep={0.01}
              onChangeFunction={handleRotateTwo}
              showIcon={false}
              showValue={false}
            />
            <SliderLabelIcon Icon={ZoomInIcon} />
            <IconValueSlider
              sliderValue={pairData.imageTwoZoomOpacity}
              sliderMin={0.1}
              sliderMax={2}
              sliderStep={0.001}
              onChangeFunction={handleZoomTwo}
              showIcon={false}
              showValue={false}
            />
          </div>
        </div>
      </div>

      <div
        className="flex flex-col items-center justify-center w-full h-full 
        overflow-hidden rounded-2xl bg-neutral-200"
        onWheel={(event) => {
          handleScrollZoom(event);
        }}
      >
        <div className="relative flex flex-col items-center justify-center w-full h-full select-none">
          {imageOneLoading && imageTwoLoading && (
            <div className={styles["loader"]} />
          )}

          <Draggable
            onDrag={(e, data) => {
              updatePositionOneOpacity(data);
            }}
            onStart={handleStart}
            onStop={() => {}}
            position={imageOnePosition()}
            positionOffset={imageOneOffset()}
            disabled={touchControls.opacity}
          >
            <img
              src={imageOneSrc}
              onTouchStart={
                touchControls.opacity
                  ? pairData.lockToggleOpacity
                    ? handleTouchStartPair
                    : handleTouchStartOne
                  : () => {}
              }
              onTouchMove={
                touchControls.opacity
                  ? pairData.lockToggleOpacity
                    ? handleTouchMovePair
                    : handleTouchMoveOne
                  : () => {}
              }
              onLoad={(e) =>
                handleImageLoad({
                  imageElement: e.target,
                  setItemLoading: setImageOneLoading(false),
                })
              }
              style={
                !imageOneLoading
                  ? imageOneStyle
                  : {
                      position: "absolute",
                      opacity: "0",
                    }
              }
              className="touch-none"
              draggable="false"
            />
          </Draggable>

          <Draggable
            onDrag={(e, data) => {
              updatePositionTwoOpacity(data);
            }}
            onStop={() => {}}
            position={imageTwoPosition()}
            positionOffset={imageTwoOffset()}
            disabled={touchControls.opacity}
          >
            <img
              src={imageTwoSrc}
              onTouchStart={
                touchControls.opacity
                  ? pairData.lockToggleOpacity
                    ? handleTouchStartPair
                    : handleTouchStartTwo
                  : () => {}
              }
              onTouchMove={
                touchControls.opacity
                  ? pairData.lockToggleOpacity
                    ? handleTouchMovePair
                    : handleTouchMoveTwo
                  : () => {}
              }
              onLoad={(e) =>
                handleImageLoad({
                  imageElement: e.target,
                  setItemLoading: setImageTwoLoading(false),
                })
              }
              style={
                !imageTwoLoading
                  ? imageTwoStyle
                  : {
                      position: "absolute",
                      opacity: "0",
                    }
              }
              className="touch-none"
              draggable="false"
            />
          </Draggable>
        </div>
      </div>
    </div>
  );
}

export default OpacityComparison;
